import type { ReactNode } from 'react';
import { Space, Tag, Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

export type RegistryQuestionnaireProgram = 'hepato' | 'dermato';

/** Consimțământul stocat pe fișa de registru (bifele + starea detectată la import). */
export interface RegistryQuestionnaireConsent {
  personalDataProcessing?: boolean | null;
  registryInclusion?: boolean | null;
  futureContact?: boolean | null;
  status?: string | null;
}

interface FieldSpec {
  /** Cheile posibile (import camelCase / wizard snake_case) — prima prezentă câștigă. */
  keys: string[];
  label: string;
  kind: 'yesno' | 'text' | 'choice' | 'checked';
  options?: Record<string, string>;
}

interface SectionSpec {
  key: string;
  title: string;
  fields: FieldSpec[];
}

/* ── Etichete hepato (texte din HepatoAnamnesisFields / anamnesisOptions) ── */

const HEPATO_SYMPTOM_LABELS: Record<string, string> = {
  rightAbdominalPain: 'Durere sau disconfort în partea dreaptă a abdomenului',
  right_abdominal_pain: 'Durere sau disconfort în partea dreaptă a abdomenului',
  jaundice: 'Icter (îngălbenirea pielii și a sclerei ochilor)',
  chronicFatigue: 'Oboseală cronică, inexplicabilă',
  chronic_fatigue: 'Oboseală cronică, inexplicabilă',
  weightLoss: 'Pierdere în greutate neintenționată',
  weight_loss: 'Pierdere în greutate neintenționată',
  lowAppetite: 'Scăderea apetitului alimentar',
  low_appetite: 'Scăderea apetitului alimentar',
  nauseaVomiting: 'Greață și/sau vărsături frecvente',
  nausea_vomiting: 'Greață și/sau vărsături frecvente',
  ascites: 'Balonare sau acumulare de lichid în abdomen (ascită)',
  pruritus: 'Prurit cutanat (mâncărimi persistente ale pielii)',
  bleedingBruising: 'Tendință crescută la sângerare sau apariția facilă a echimozelor (vânătăi)',
  bleeding_bruising: 'Tendință crescută la sângerare sau apariția facilă a echimozelor (vânătăi)',
};

const HEPATO_PERSONAL_LABELS: Record<string, string> = {
  hepatitisB: 'Hepatită virală B',
  hepatitis_b: 'Hepatită virală B',
  hepatitisC: 'Hepatită virală C',
  hepatitis_c: 'Hepatită virală C',
  cirrhosis: 'Ciroză hepatică',
  nafld: 'Boală hepatică grasă non-alcoolică (NAFLD)',
  geneticLiverDisease: 'Hemocromatoză sau alte boli hepatice genetice',
  genetic_liver_disease: 'Hemocromatoză sau alte boli hepatice genetice',
  diabetes: 'Diabet zaharat (tip 1 / tip 2)',
  hypertension: 'Hipertensiune arterială',
  oncologicOrHormonalTreatments: 'Tratamente hormonale, chimioterapie, radioterapie',
  oncologicOrHormonal: 'Tratamente hormonale, chimioterapie, radioterapie',
  oncologic_or_hormonal_treatments: 'Tratamente hormonale, chimioterapie, radioterapie',
};

const HEPATO_FAMILY_LABELS: Record<string, string> = {
  liverCancer: 'Cancer hepatic',
  liver_cancer: 'Cancer hepatic',
  hepatitis: 'Hepatită B/C',
  cirrhosis: 'Ciroză hepatică',
  diabetes: 'Diabet zaharat',
};

const ALCOHOL_LABELS: Record<string, string> = {
  none: 'Nu consum alcool',
  occasional: 'Ocazional',
  frequent: 'Frecvent',
};

const GENERAL_DATA_FIELDS: FieldSpec[] = [
  { keys: ['cnp'], label: 'CNP', kind: 'text' },
  { keys: ['fullName'], label: 'Nume complet', kind: 'text' },
  { keys: ['birthDate'], label: 'Data nașterii', kind: 'text' },
  { keys: ['age'], label: 'Vârstă', kind: 'text' },
  { keys: ['weightKg'], label: 'Greutate (kg)', kind: 'text' },
  { keys: ['heightCm'], label: 'Înălțime (cm)', kind: 'text' },
  { keys: ['profession'], label: 'Profesia', kind: 'text' },
  { keys: ['medicalUnitCode'], label: 'Cod unitate medicală / secție', kind: 'text' },
  { keys: ['occupationalExposure'], label: 'Locul de muncă (expunere la toxine/substanțe chimice)', kind: 'text' },
];

const HEPATO_SECTIONS: SectionSpec[] = [
  { key: 'generalData', title: 'I. Date generale', fields: GENERAL_DATA_FIELDS },
  {
    key: 'currentSymptoms',
    title: 'II. Simptome actuale',
    fields: [
      { keys: ['checked', 'items'], label: 'Simptome bifate', kind: 'checked', options: HEPATO_SYMPTOM_LABELS },
      { keys: ['other'], label: 'Alte simptome / detalii', kind: 'text' },
    ],
  },
  {
    key: 'personalHistory',
    title: 'III. Istoric medical personal',
    fields: [
      { keys: ['checked', 'conditions'], label: 'Afecțiuni diagnosticate', kind: 'checked', options: HEPATO_PERSONAL_LABELS },
      { keys: ['oncologicHistory'], label: 'Antecedente oncologice — tipul de cancer', kind: 'text' },
    ],
  },
  {
    key: 'familyHistory',
    title: 'IV. Istoric medical familial',
    fields: [
      { keys: ['checked', 'conditions'], label: 'Afecțiuni în familie', kind: 'checked', options: HEPATO_FAMILY_LABELS },
      { keys: ['otherChronicDiseases'], label: 'Alte boli cronice relevante / detalii', kind: 'text' },
    ],
  },
  {
    key: 'riskFactors',
    title: 'V. Factori de risc și stil de viață',
    fields: [
      { keys: ['alcoholUse'], label: 'Consumul de alcool', kind: 'choice', options: ALCOHOL_LABELS },
      { keys: ['dailyAlcoholMl'], label: 'Cantitate zilnică de alcool (ml)', kind: 'text' },
      { keys: ['smoking'], label: 'Fumatul', kind: 'yesno' },
      { keys: ['smokingYears'], label: 'De câți ani fumați', kind: 'text' },
      { keys: ['cigarettesPerDay'], label: 'Țigări/zi', kind: 'text' },
      { keys: ['toxicExposurePerformed'], label: 'Expunerea la substanțe toxice/chimice (ex.: pesticide, solvenți, aflatoxine)', kind: 'yesno' },
      { keys: ['toxicExposureDetails'], label: 'Substanțele/tipurile de expunere', kind: 'text' },
      { keys: ['transfusionsBefore1992'], label: 'Transfuzii de sânge efectuate înainte de 1992', kind: 'yesno' },
      { keys: ['aflatoxinFoodUse'], label: 'Consum frecvent de alimente posibil contaminate cu aflatoxine (arahide, porumb mucegăit etc.)', kind: 'yesno' },
      { keys: ['hepatitisBVaccination'], label: 'Vaccinare împotriva hepatitei B', kind: 'yesno' },
      { keys: ['hepatitisBVaccinationYear'], label: 'Anul vaccinării', kind: 'text' },
      { keys: ['highRiskTravelPerformed'], label: 'Călătorii în zone cu risc ridicat de hepatită virală', kind: 'yesno' },
      { keys: ['highRiskTravelCountries'], label: 'Țări vizitate', kind: 'text' },
      { keys: ['tattoos'], label: 'Tatuaje', kind: 'yesno' },
      { keys: ['multipleSexualPartners'], label: 'Mai mult de un partener sexual în ultimele 6 luni', kind: 'yesno' },
    ],
  },
  {
    key: 'previousExams',
    title: 'VI. Examinări anterioare',
    fields: [
      { keys: ['liverFunctionTestsPerformed'], label: 'Analize de laborator pentru funcția hepatică (TGP, TGO, GGT, bilirubină etc.)', kind: 'yesno' },
      { keys: ['liverFunctionTestsDate'], label: 'Data recoltării', kind: 'text' },
      { keys: ['liverFunctionTestsResults'], label: 'Rezultate analize hepatice', kind: 'text' },
      { keys: ['imagingPerformed'], label: 'Investigații imagistice hepatice (ecografie, CT, RMN, elastografie hepatică)', kind: 'yesno' },
      { keys: ['imagingDate'], label: 'Data investigației', kind: 'text' },
      { keys: ['imagingResults'], label: 'Rezultate investigații imagistice hepatice', kind: 'text' },
    ],
  },
];

/* ── Etichete dermato (texte din Step2Anamnesis; cheile din extracția de import) ── */

const SKIN_CANCER_TYPE_LABELS: Record<string, string> = {
  melanoma: 'Melanom',
  basalCell: 'Carcinom bazocelular',
  basal_cell_carcinoma: 'Carcinom bazocelular',
  squamousCell: 'Carcinom scuamocelular',
  squamous_cell_carcinoma: 'Carcinom scuamocelular',
  other: 'Altul',
};

const SUN_EXPOSURE_LABELS: Record<string, string> = {
  lt30min: '< 30 min',
  under_30_min: '< 30 min',
  '30min_1h': '30 min – 1 oră',
  '30_to_60_min': '30 min – 1 oră',
  '1_3h': '1–3 ore',
  '1_to_3_hours': '1–3 ore',
  gt3h: '> 3 ore',
  over_3_hours: '> 3 ore',
};

const SKIN_TYPE_LABELS: Record<string, string> = {
  TYPE_I: 'Tip I — piele foarte albă, se arde întotdeauna, nu se bronzează',
  TYPE_II: 'Tip II — piele deschisă, se arde ușor, se bronzează slab',
  TYPE_III: 'Tip III — piele albă mai închisă, se bronzează după o arsură inițială',
  TYPE_IV: 'Tip IV — piele brun deschis, se arde minim, se bronzează ușor',
  TYPE_V: 'Tip V — piele brună, se arde rar, se bronzează intens',
  TYPE_VI: 'Tip VI — piele brun închis / neagră, nu se arde niciodată',
};

const SKIN_CHANGES_LABELS: Record<string, string> = {
  newMoles: 'Alunițe noi',
  new_moles: 'Alunițe noi',
  colorChange: 'Modificări de culoare',
  color_changes: 'Modificări de culoare',
  growth: 'Creștere în dimensiuni',
  bleeding: 'Sângerare',
  itching: 'Mâncărime',
  persistentUlcers: 'Ulcerații persistente',
  ulcerations: 'Ulcerații persistente',
  other: 'Altul',
};

const SKIN_EXAM_LABELS: Record<string, string> = {
  monthly: 'Lunar',
  every6months: 'La 6 luni',
  six_months: 'La 6 luni',
  yearly: 'Anual',
  never: 'Niciodată',
};

const DERMATO_SECTIONS: SectionSpec[] = [
  { key: 'generalData', title: 'I. Date generale', fields: GENERAL_DATA_FIELDS },
  {
    key: 'medicalHistoryDetails',
    title: 'II. Istoric medical personal',
    fields: [
      { keys: ['priorSkinCancer', 'previousSkinCancer'], label: 'Ați fost diagnosticat(ă) anterior cu cancer de piele?', kind: 'yesno' },
      { keys: ['priorSkinCancerType'], label: 'Tip cancer de piele diagnosticat', kind: 'choice', options: SKIN_CANCER_TYPE_LABELS },
      { keys: ['priorSkinCancerTypeOther', 'previousSkinCancerOther'], label: 'Alt tip de cancer de piele', kind: 'text' },
      { keys: ['priorSkinCancerTreatments', 'previousSkinCancerTreatments'], label: 'Tratamente urmate pentru cancerul de piele', kind: 'text' },
      { keys: ['otherCancer', 'previousOtherCancer'], label: 'Ați fost diagnosticat(ă) anterior cu un alt tip de cancer?', kind: 'yesno' },
      { keys: ['otherCancerType', 'previousOtherCancerType'], label: 'Tipul celuilalt cancer', kind: 'text' },
      { keys: ['otherCancerTreatments', 'previousOtherCancerTreatments'], label: 'Tratamente pentru celălalt cancer', kind: 'text' },
      { keys: ['geneticMutation'], label: 'Mutație genetică predispozantă la cancer de piele?', kind: 'yesno' },
      { keys: ['geneticMutationType'], label: 'Mutația genetică', kind: 'text' },
      { keys: ['chronicSkinConditions', 'chronicDermatologicConditions'], label: 'Aveți alte afecțiuni dermatologice cronice?', kind: 'yesno' },
      { keys: ['chronicSkinConditionsDetail', 'chronicDermatologicConditionDetails'], label: 'Afecțiunile dermatologice cronice', kind: 'text' },
      { keys: ['autoimmuneOrImmunocompromised', 'autoimmuneOrImmunosuppression'], label: 'Aveți boli autoimune sau un sistem imunitar slăbit?', kind: 'yesno' },
      { keys: ['photosensitizingMedications', 'photosensitizingMedication'], label: 'Luați medicamente care cresc sensibilitatea pielii la soare?', kind: 'yesno' },
      { keys: ['photosensitizingMedicationsDetail', 'photosensitizingMedicationDetails'], label: 'Medicamentele fotosensibilizante', kind: 'text' },
    ],
  },
  {
    key: 'familyHistoryDetails',
    title: 'III. Istoric medical familial',
    fields: [
      { keys: ['familySkinCancer'], label: 'Există cazuri de cancer de piele în familie?', kind: 'yesno' },
      { keys: ['familySkinCancerDetail', 'familySkinCancerDetails'], label: 'Rudele afectate', kind: 'text' },
      { keys: ['otherGeneticConditions', 'familyGeneticDisease'], label: 'Există alte afecțiuni genetice asociate cancerului de piele în familie?', kind: 'yesno' },
      { keys: ['otherGeneticConditionsDetail', 'familyGeneticDiseaseDetails'], label: 'Rudele și tipul de afecțiuni', kind: 'text' },
    ],
  },
  {
    key: 'uvRiskFactors',
    title: 'IV. Expunere la radiații UV și factori de risc',
    fields: [
      { keys: ['dailySunExposure', 'sunExposureDaily'], label: 'Cât timp petreceți zilnic la soare?', kind: 'choice', options: SUN_EXPOSURE_LABELS },
      { keys: ['severeSunburns'], label: 'Ați suferit arsuri solare severe cu bășici?', kind: 'yesno' },
      { keys: ['severeSunburnsCount', 'severeSunburnEpisodes'], label: 'Număr de episoade de arsuri solare', kind: 'text' },
      { keys: ['dailySunscreen'], label: 'Utilizați protecție solară zilnic?', kind: 'yesno' },
      { keys: ['sunscreenSpf'], label: 'Factorul SPF', kind: 'text' },
      { keys: ['tanningOilsWithoutSpf', 'tanningWithoutSpf'], label: 'Utilizați uleiuri/creme fără SPF pentru bronzare rapidă?', kind: 'yesno' },
      { keys: ['tanningSalon', 'tanningSalonUse'], label: 'Frecventați saloane de bronzare artificială?', kind: 'yesno' },
      { keys: ['tanningSalonFrequency'], label: 'Frecvența saloanelor de bronzare', kind: 'text' },
      { keys: ['livedHighUvZone', 'highUvResidence'], label: 'Ați locuit într-o zonă cu radiații solare intense (tropice, munți)?', kind: 'yesno' },
      { keys: ['skinType'], label: 'Tipul de piele (Fitzpatrick)', kind: 'choice', options: SKIN_TYPE_LABELS },
      { keys: ['chemicalExposure', 'toxicChemicalExposure'], label: 'Ați fost expus la substanțe chimice toxice (arsenic, hidrocarburi, gudron…)?', kind: 'yesno' },
      { keys: ['chemicalExposureDetail', 'toxicChemicalExposureDetails'], label: 'Substanțele chimice', kind: 'text' },
    ],
  },
  {
    key: 'skinEvaluation',
    title: 'V. Evaluarea pielii și simptome',
    fields: [
      { keys: ['skinChanges6Months', 'recentSkinChanges'], label: 'Ați observat modificări ale pielii în ultimele 6 luni?', kind: 'yesno' },
      { keys: ['skinChangesTypes', 'skinChanges'], label: 'Tipul modificărilor', kind: 'checked', options: SKIN_CHANGES_LABELS },
      { keys: ['skinChangesOther'], label: 'Alte modificări ale pielii', kind: 'text' },
      { keys: ['reddishScalyLesions', 'nonHealingLesions'], label: 'Ați observat pete roșiatice, solzoase sau leziuni care nu se vindecă?', kind: 'yesno' },
      { keys: ['painOrSensitivity'], label: 'Aveți durere sau sensibilitate crescută în zonele afectate?', kind: 'yesno' },
      { keys: ['skinExamFrequency', 'selfExamFrequency'], label: 'Cât de des vă examinați pielea?', kind: 'choice', options: SKIN_EXAM_LABELS },
    ],
  },
  {
    key: 'lifestyle',
    title: 'VI. Stil de viață și obiceiuri',
    fields: [
      { keys: ['smoking'], label: 'Fumați?', kind: 'yesno' },
      { keys: ['smokingYears'], label: 'De câți ani fumați', kind: 'text' },
      { keys: ['alcohol', 'alcoholUse'], label: 'Consumați alcool?', kind: 'yesno' },
      { keys: ['alcoholFrequency'], label: 'Frecvența consumului de alcool', kind: 'text' },
      { keys: ['balancedDiet'], label: 'Aveți o dietă echilibrată, bogată în vitamine și antioxidanți?', kind: 'yesno' },
      { keys: ['dermatologicalCheckups', 'regularDermatologyChecks'], label: 'Mergeți la controale dermatologice regulate?', kind: 'yesno' },
      { keys: ['lastDermatologicalCheckup', 'lastDermatologyCheckDate'], label: 'Ultimul control dermatologic', kind: 'text' },
      { keys: ['naturalSkincareProducts', 'naturalSkinCareProducts'], label: 'Utilizați produse de îngrijire a pielii cu ingrediente naturale?', kind: 'yesno' },
    ],
  },
  {
    key: 'observations',
    title: 'VII. Observații suplimentare',
    fields: [{ keys: ['notes'], label: 'Observații', kind: 'text' }],
  },
];

const CONSENT_STATUS_LABELS: Record<string, string> = {
  TICKED: 'bifat',
  SIGNED_NO_TICKS: 'semnat fără bife',
  MISSING: 'lipsă',
};

/* ── Normalizări ── */

const isRecord = (v: unknown): v is Record<string, unknown> =>
  Boolean(v) && typeof v === 'object' && !Array.isArray(v);

const asStringArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

const YES_VALUES = new Set(['yes', 'da', 'true', '1']);
const NO_VALUES = new Set(['no', 'nu', 'false', '0']);

function toYesNo(value: unknown): 'yes' | 'no' | 'unknown' | null {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (YES_VALUES.has(v)) return 'yes';
    if (NO_VALUES.has(v)) return 'no';
    if (v === 'unknown') return 'unknown';
  }
  return null;
}

/** Deduce programul din cheile secțiunilor (când lotul nu e cunoscut). */
export function inferQuestionnaireProgram(
  sections: Record<string, unknown>,
): RegistryQuestionnaireProgram {
  const dermatoKeys = ['medicalHistoryDetails', 'familyHistoryDetails', 'uvRiskFactors', 'skinEvaluation', 'lifestyle'];
  return dermatoKeys.some((k) => sections[k] !== undefined) ? 'dermato' : 'hepato';
}

/* ── Piese de randare ── */

const YesNoBadge = ({ value }: { value: unknown }) => {
  const yn = toYesNo(value);
  if (yn === 'yes') return <Tag color="green">DA</Tag>;
  if (yn === 'no') return <Tag>NU</Tag>;
  if (yn === 'unknown') return <Tag color="blue">Nu știu</Tag>;
  if (value !== undefined && value !== null && value !== '') return <Text>{String(value)}</Text>;
  return <Text type="secondary">—</Text>;
};

const CheckedList = ({ items, labels }: { items: string[]; labels: Record<string, string> }) => (
  <Space wrap size={[4, 4]}>
    {items.map((item) => (
      <Tag key={item} color="green" icon={<CheckOutlined />}>
        {labels[item] ?? item}
      </Tag>
    ))}
  </Space>
);

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div style={{ marginBottom: 6 }}>
    <Text type="secondary">{label}: </Text>
    {children}
  </div>
);

function FieldValue({ field, value }: { field: FieldSpec; value: unknown }) {
  if (field.kind === 'checked') {
    const items = asStringArray(value);
    return items.length ? <CheckedList items={items} labels={field.options ?? {}} /> : <Text type="secondary">niciuna bifată</Text>;
  }
  if (field.kind === 'yesno') return <YesNoBadge value={value} />;
  if (field.kind === 'choice') {
    if (value === undefined || value === null || value === '') return <Text type="secondary">—</Text>;
    return <Text>{field.options?.[String(value)] ?? String(value)}</Text>;
  }
  return <Text>{String(value)}</Text>;
}

function SectionView({ spec, section }: { spec: SectionSpec; section: Record<string, unknown> }) {
  const usedKeys = new Set(spec.fields.flatMap((f) => f.keys));
  const rows: ReactNode[] = [];

  for (const field of spec.fields) {
    const key = field.keys.find((k) => section[k] !== undefined && section[k] !== null && section[k] !== '');
    const value = key ? section[key] : undefined;
    // text/checked se afișează doar dacă au conținut; da/nu și alegerile arată și „—” (necompletat)
    if (field.kind === 'text' && (value === undefined || value === '')) continue;
    if (field.kind === 'checked' && asStringArray(value).length === 0) continue;
    rows.push(
      <Row key={field.label} label={field.label}>
        <FieldValue field={field} value={value} />
      </Row>,
    );
  }

  // câmpuri extrase care nu sunt în schema cunoscută — nu le ascundem
  for (const [key, value] of Object.entries(section)) {
    if (usedKeys.has(key)) continue;
    if (typeof value === 'string' || typeof value === 'number') {
      rows.push(<Row key={key} label={key}><Text>{String(value)}</Text></Row>);
    } else if (Array.isArray(value) && asStringArray(value).length > 0) {
      rows.push(<Row key={key} label={key}><CheckedList items={asStringArray(value)} labels={{}} /></Row>);
    }
  }

  if (rows.length === 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <Title level={5} style={{ marginTop: 8 }}>{spec.title}</Title>
      {rows}
    </div>
  );
}

const CONSENT_ITEMS: Array<{ key: keyof RegistryQuestionnaireConsent; label: string }> = [
  { key: 'personalDataProcessing', label: 'Consimțământ prelucrare date personale' },
  { key: 'registryInclusion', label: 'Consimțământ includere în registrul transfrontalier' },
  { key: 'futureContact', label: 'Consimțământ contact pentru follow-up' },
];

export function ConsentView({ consent }: { consent: RegistryQuestionnaireConsent }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <Title level={5} style={{ marginTop: 8 }}>Consimțământ</Title>
      <Space wrap size={[8, 4]} style={{ marginBottom: 6 }}>
        {CONSENT_ITEMS.map(({ key, label }) =>
          consent[key] ? (
            <Tag key={key} color="green" icon={<CheckOutlined />}>{label}</Tag>
          ) : (
            <Tag key={key}>{label} — nebifat</Tag>
          ),
        )}
      </Space>
      {consent.status && (
        <div>
          <Text type="secondary">Stare consimțământ: </Text>
          <Text>{CONSENT_STATUS_LABELS[consent.status] ?? consent.status}</Text>
        </div>
      )}
    </div>
  );
}

interface Props {
  program: RegistryQuestionnaireProgram;
  /** Secțiunile chestionarului (JSONB); chei lipsă sau parțiale sunt tolerate. */
  sections: Record<string, unknown> | null | undefined;
  /** Consimțământul fișei; dacă lipsește, se caută `sections.consent`. */
  consent?: RegistryQuestionnaireConsent | null;
}

/** Chestionarul importat din documente, randat structurat (read-only). */
export default function RegistryQuestionnaireView({ program, sections, consent }: Props) {
  const data = isRecord(sections) ? sections : {};
  const specs = program === 'dermato' ? DERMATO_SECTIONS : HEPATO_SECTIONS;
  const consentData = consent ?? (isRecord(data.consent) ? (data.consent as RegistryQuestionnaireConsent) : null);

  const rendered = specs
    .filter((spec) => isRecord(data[spec.key]) && Object.keys(data[spec.key] as object).length > 0)
    .map((spec) => (
      <SectionView key={spec.key} spec={spec} section={data[spec.key] as Record<string, unknown>} />
    ))
    .filter(Boolean);

  if (rendered.length === 0 && !consentData) {
    return <Text type="secondary">Chestionarul nu conține date.</Text>;
  }

  return (
    <div data-testid="registry-questionnaire">
      {rendered}
      {consentData && <ConsentView consent={consentData} />}
    </div>
  );
}
